import React, { useRef, useEffect } from 'react';
import ChatContainer from './components/ChatContainer';
import ActionToolbar from './components/ActionToolbar';
import Orb from './components/Orb';
import RippleGrid from './components/RippleGrid';
import useChatBot from './hooks/useChatBot';
import './styles/chat.css';

function App() {
  const orbRef = useRef(null);
  const orbComplexRef = useRef(null);
  const {
    messages,
    isTyping,
    currentActions,
    sendButtonDisabled,
    inputDisabled,
    inputPlaceholder,
    userTag,
    sendMessage
  } = useChatBot();

  // Function to calculate and update orb and chat positions based on screen size
  const updatePositions = () => {
    const orbComplex = orbComplexRef.current;
    const chatWrapper = document.querySelector('.chat-interface-wrapper');
    
    if (!orbComplex || !chatWrapper) return;
    
    // Get current orb size and chat multiplier based on screen width
    let orbSize, chatMultiplier;
    if (window.innerWidth <= 480) {
      orbSize = 140; // Small mobile
      chatMultiplier = 1.2; // Closest positioning for small mobile
    } else if (window.innerWidth <= 768) {
      orbSize = 150; // Mobile/tablet
      chatMultiplier = 1.3; // Closer positioning for mobile
    } else {
      orbSize = 150; // Desktop
      chatMultiplier = 2; // Desktop positioning (matches current JS)
    }
    
    // Calculate orb up offset (0.7x orb height)
    const orbUpOffset = orbSize * 0.7;
    
    // Calculate chat top offset based on screen size
    const chatTopOffset = orbSize * chatMultiplier;
    
    // Update orb position
    orbComplex.style.setProperty('--orb-size', `${orbSize}px`);
    orbComplex.style.setProperty('--orb-height', `${orbSize}px`);
    orbComplex.style.setProperty('--orb-up-offset', `${orbUpOffset}px`);
    orbComplex.style.setProperty('--complex-translate-y', `-${orbUpOffset}px`);
    
    // Update chat position
    chatWrapper.style.setProperty('--orb-height', `${orbSize}px`);
    chatWrapper.style.setProperty('--chat-top-offset', `${chatTopOffset}px`);
    chatWrapper.style.marginTop = `${chatTopOffset}px`;
  };

  // Expose orb controls globally for easy access from anywhere in the app
  useEffect(() => {
    // Make orb controls available globally
    window.orbControls = orbRef.current;
    
    // Make chat expansion controls available globally
    window.chatControls = {
      expand: () => {
        const chatWrapper = document.querySelector('.chat-interface-wrapper');
        if (chatWrapper) {
          chatWrapper.classList.add('expanded');
        }
      },
      collapse: () => {
        const chatWrapper = document.querySelector('.chat-interface-wrapper');
        if (chatWrapper) {
          chatWrapper.classList.remove('expanded');
        }
      },
      toggle: () => {
        const chatWrapper = document.querySelector('.chat-interface-wrapper');
        if (chatWrapper) {
          chatWrapper.classList.toggle('expanded');
        }
      }
    };
    
    // Make positioning controls available globally
    window.positioningControls = {
      updatePositions: updatePositions,
      getCurrentOrbSize: () => {
        if (window.innerWidth <= 480) return 140;
        if (window.innerWidth <= 768) return 150;
        return 150;
      }
    };
    
    // Initial position calculation
    updatePositions();
    
    // Add resize listener
    const handleResize = () => {
      updatePositions();
    };
    
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
    
    // Make orb complex movement and scaling controls available globally
    window.orbComplexControls = {
      // Smooth scaling function
      setScale: (scale, translateY = null, duration = 500) => {
        if (orbComplexRef.current) {
          orbComplexRef.current.style.transition = `transform ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
          orbComplexRef.current.style.setProperty('--complex-scale', scale);
          if (translateY !== null) {
            orbComplexRef.current.style.setProperty('--complex-translate-y', `${translateY}px`);
          }
          setTimeout(() => {
            if (orbComplexRef.current) {
              orbComplexRef.current.style.transition = '';
            }
          }, duration);
        }
      },
      
      // Smooth positioning function
      setPosition: (translateY, duration = 500) => {
        if (orbComplexRef.current) {
          orbComplexRef.current.style.transition = `transform ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
          orbComplexRef.current.style.setProperty('--complex-translate-y', `${translateY}px`);
          setTimeout(() => {
            if (orbComplexRef.current) {
              orbComplexRef.current.style.transition = '';
            }
          }, duration);
        }
      },
      
      // Preset scaling options
      scaleToDesktop: () => window.orbComplexControls.setScale(1, -100),
      scaleToTablet: () => window.orbComplexControls.setScale(0.84, -60),
      scaleToMobile: () => window.orbComplexControls.setScale(0.76, -45),
      
      // Legacy movement functions (updated to work with new transform system)
      moveLeft: () => {
        if (orbComplexRef.current) {
          orbComplexRef.current.classList.add('translating', 'move-left');
          setTimeout(() => orbComplexRef.current?.classList.remove('translating'), 500);
        }
      },
      moveRight: () => {
        if (orbComplexRef.current) {
          orbComplexRef.current.classList.add('translating', 'move-right');
          setTimeout(() => orbComplexRef.current?.classList.remove('translating'), 500);
        }
      },
      moveUp: () => {
        if (orbComplexRef.current) {
          orbComplexRef.current.classList.add('translating', 'move-up');
          setTimeout(() => orbComplexRef.current?.classList.remove('translating'), 500);
        }
      },
      moveDown: () => {
        if (orbComplexRef.current) {
          orbComplexRef.current.classList.add('translating', 'move-down');
          setTimeout(() => orbComplexRef.current?.classList.remove('translating'), 500);
        }
      },
      scaleSmall: () => window.orbComplexControls.setScale(0.6),
      scaleLarge: () => window.orbComplexControls.setScale(1.4),
      reset: () => {
        if (orbComplexRef.current) {
          orbComplexRef.current.classList.add('translating');
          orbComplexRef.current.classList.remove('move-left', 'move-right', 'move-up', 'move-down', 'scale-small', 'scale-large');
          window.orbComplexControls.setScale(1, -100);
          setTimeout(() => orbComplexRef.current?.classList.remove('translating'), 500);
        }
      }
    };
  }, []);

  // Monitor AI typing state and animate orb accordingly
  useEffect(() => {
    if (!orbRef.current) return;

    if (isTyping) {
      // AI is responding - change to purple hue (253) and increase intensity
      orbRef.current.animateTo({
        hue: 253,
        hoverIntensity: 0.15
      }, 800); // Smoother, longer transition when starting
    } else {
      // AI finished responding - return to original state
      orbRef.current.animateTo({
        hue: 0,
        hoverIntensity: 0
      }, 1200); // Even smoother transition when finishing
    }
  }, [isTyping]);

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      width: '100%', 
      height: '100vh',
      height: '100dvh', // Dynamic viewport height for mobile
      padding: '10px 20px 0 20px',
      boxSizing: 'border-box',
      position: 'relative',
      backgroundColor: '#000000',
      overflow: 'hidden' // Prevent scrolling on the main container
    }}>
      
      {/* Unified Orb Complex - contains RippleGrid, Orb, and ActionToolbar */}
      <div ref={orbComplexRef} className="orb-complex-container">
        {/* Background RippleGrid */}
        <div className="ripple-grid-wrapper">
          <RippleGrid
            enableRainbow={false}
            gridColor="#4a90e2"
            rippleIntensity={0.06}
            rippleSpeed={0.6}
            gridSize={20}
            gridThickness={10}
            fadeDistance={1.0}
            vignetteStrength={1}
            glowIntensity={0.4}
            opacity={0.8}
            gridRotation={0}
            mouseInteraction={true}
            mouseInteractionRadius={1.5}
            isTyping={isTyping}
          />
        </div>
        
        {/* Black circle to hide RippleGrid behind Orb */}
        <div className="orb-background-mask" />
        
        {/* Profile Orb with Action Items */}
        <div className="profile-orb-container">
          <Orb
            ref={orbRef}
            initialHoverIntensity={0}
            initialRotateOnHover={true}
            initialHue={0}
            initialForceHoverState={true}
          />
          <ActionToolbar actions={currentActions} orbRef={orbRef} />
        </div>
      </div>
      
      {/* Chat Interface */}
      <div 
        className="chat-interface-wrapper"
        onClick={() => window.chatControls?.toggle()}
        style={{ cursor: 'pointer' }}
        title="Click to expand/collapse chat"
      >
        <ChatContainer
          messages={messages}
          isTyping={isTyping}
          onSendMessage={sendMessage}
          sendButtonDisabled={sendButtonDisabled}
          inputDisabled={inputDisabled}
          inputPlaceholder={inputPlaceholder}
          userTag={userTag}
        />
      </div>
    </div>
  );
}

export default App;
